import { useEffect } from "react"
import { Controller, useFormContext, useWatch } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useFragment } from "react-relay"
import { Banner } from "@/components/ui/banner"
import { TextArea } from "@/components/ui/input"
import { REASON_FOR_ENCOUNTER_MAX_LENGTH } from "@/lib/constants"
import { getServiceMapping } from "@/lib/fetch-service-mappings"
import { ServiceSelect } from "@/modules/patients/agenda/teleconsultation/booking/components/service-select"
import { isMedgateConnect } from "@/modules/patients/agenda/teleconsultation/booking/constants/medgate-connect"
import { TeleconsultationForm } from "@/modules/patients/agenda/teleconsultation/booking/teleconsultation-schema"
import { PatientFragment$key } from "@/modules/patients/services/__generated__/PatientFragment.graphql"
import { PatientFragment } from "@/modules/patients/services/PatientFragment"
import { CallLineBanner } from "@/modules/telephony/call-line-banner"
import { RequestType } from "@/types/__generated__/union-types"
import { UI_DISPLAY_LANGUAGES } from "@/types/UiDisplayLanguage"

const REQUEST_OPTIONS: RequestType[] = [
  "NONE",
  "SICK_LEAVE_CERTIFICATE",
  "PRESCRIPTION",
  "REFERRAL",
  "ONBOARDING_CALL",
  "PLANNED_SELF_REFERRAL",
]

const REQUEST_OPTIONS_FOR_MINOR: RequestType[] = [
  "NONE",
  "SICK_LEAVE_CERTIFICATE",
  "SICK_LEAVE_FOR_LEGAL_GUARDIAN",
  "PRESCRIPTION",
  "REFERRAL",
  "ONBOARDING_CALL",
  "PLANNED_SELF_REFERRAL",
]

interface ReasonForEncounterSectionProps {
  patientFragmentRef: PatientFragment$key
  isRestrictedByAppBooking?: boolean
}

const ReasonForEncounterSection = ({
  patientFragmentRef,
  isRestrictedByAppBooking = false,
}: ReasonForEncounterSectionProps) => {
  const { t } = useTranslation()
  const { control, setValue } = useFormContext<TeleconsultationForm>()
  const serviceWatch = useWatch({ control, name: "service" })
  const specialtyWatch = useWatch({ control, name: "specialty" })
  const currentRequestType = useWatch({ control, name: "requestType" })

  const patient = useFragment<PatientFragment$key>(PatientFragment, patientFragmentRef)

  // Ensure current value is always available (preserves mobile-only types when editing)
  const baseOptions = (patient.age?.years ?? 0) < 18 ? REQUEST_OPTIONS_FOR_MINOR : REQUEST_OPTIONS
  const availableOptions = baseOptions.includes(currentRequestType) ? baseOptions : [currentRequestType, ...baseOptions]

  // Sorted by the German label, so the order stays the same whatever the UI language is
  const requestOptions = availableOptions
    .map((e) => ({
      value: e,
      label: t(`scheduling.waitingRoom.requestType.${e}`),
      labelGerman: t(`scheduling.waitingRoom.requestType.${e}`, { lng: UI_DISPLAY_LANGUAGES.GERMAN }),
    }))
    .toSorted((e1, e2) => {
      if (e1.value === "NONE") return -1
      if (e2.value === "NONE") return 1
      return e1.labelGerman.localeCompare(e2.labelGerman)
    })

  const isRequestDisplayed =
    !isMedgateConnect(serviceWatch) &&
    getServiceMapping().serviceMappings.find(
      (mapping) => mapping.service === serviceWatch && mapping.specialty === specialtyWatch,
    )?.isRequestRelevant

  useEffect(() => {
    if (!isRequestDisplayed) {
      setValue("requestType", "NONE")
    }
  }, [isRequestDisplayed, setValue])

  return (
    <>
      {isRequestDisplayed && (
        <Controller
          control={control}
          name="requestType"
          render={({ field: { value, onChange } }) => (
            <ServiceSelect
              selectedValue={{
                value: value ?? "NONE",
                label: t(`scheduling.waitingRoom.requestType.${value}`),
              }}
              options={requestOptions}
              label={t("patient.agenda.request")}
              onSelect={(option) => onChange(option.value)}
              testId="consultation-request"
              disabled={isRestrictedByAppBooking}
            />
          )}
        />
      )}
      {isRequestDisplayed && isRestrictedByAppBooking && (
        <Banner type="inline" variant="info" data-testid="consultation-request-app-booked-restriction">
          {t("patient.agenda.appBookedRestriction.request")}
        </Banner>
      )}
      <Controller
        control={control}
        name="reason"
        render={({ formState: { errors }, field }) => (
          <TextArea
            {...field}
            data-testid="reasonForEncounter"
            className="max-h-56"
            maxLength={REASON_FOR_ENCOUNTER_MAX_LENGTH}
            label={t("episodeOfCare.progressNote.reasonForEncounter")}
            required
            translateError
            error={errors.reason?.message}
          />
        )}
      />
      <CallLineBanner className="rounded-md px-4" testId="booking-dialog-call-line-banner" />
    </>
  )
}

export { ReasonForEncounterSection }
